import type { ComponentProps, MouseEvent as ReactMouseEvent, RefObject } from 'react'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import Modal from 'LuxKit/Modal/Modal'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import Backdrop from 'components/utils/Backdrop'
import { useScreenSizeUp } from 'hooks/useScreenSize'

export type TravellerSelectionOccupancyPickerProps = Omit<
  ComponentProps<typeof OccupancyPicker>,
  'occupancies' | 'onChange'
>

type DropdownSheetProps = ComponentProps<typeof DropdownSheet>

const MobileModalSummaryInset = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding-inline: ${rem(20)};
`

export interface TravellerSelectionModalProps extends TravellerSelectionOccupancyPickerProps {
  anchorRef: RefObject<HTMLElement | null>
  triggerRef: RefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
  occupancies: ComponentProps<typeof OccupancyPicker>['occupancies']
  onChange: NonNullable<ComponentProps<typeof OccupancyPicker>['onChange']>
  boundaryRef?: DropdownSheetProps['boundaryRef']
  dropdownSize?: DropdownSheetProps['size']
  placement?: DropdownSheetProps['placement']
  portaled?: DropdownSheetProps['portaled']
}

function TravellerSelectionModal(props: TravellerSelectionModalProps) {
  const {
    anchorRef,
    triggerRef,
    isOpen,
    onClose,
    occupancies,
    onChange,
    disableSaleUnit,
    saleUnit,
    boundaryRef,
    dropdownSize,
    placement,
    portaled,
    ...occupancyPickerProps
  } = props
  const intl = useIntl()
  const isTabletUp = useScreenSizeUp('tablet')
  const useFullScreenMobileModal = !isTabletUp

  const title = intl.formatMessage({
    id: 'S6Irt6R7F6vv5eFVQupy',
    defaultMessage: 'Who’s going?',
    description: 'Heading for traveler selection step in cruise checkout',
  })

  const applyLabel = intl.formatMessage({
    id: 'sWpTAGOfv-VlyomL8PMk',
    defaultMessage: 'Apply',
    description: 'Apply button text for cruise filters in the cruise offer or booking section',
  })

  const formattedSaleUnit = useMemo(() => {
    if (disableSaleUnit) {
      return undefined
    }
    return saleUnit
  }, [disableSaleUnit, saleUnit])

  const handleApply = useCallback(() => {
    onClose()
  }, [onClose])

  const handleModalClose = useCallback(
    (_event: ReactMouseEvent<HTMLElement>) => {
      onClose()
    },
    [onClose],
  )

  const occupancySummary = (
    <Group direction="horizontal" horizontalAlign="end">
      <BodyText variant="large">
        <FormattedOccupancy
          adultLabel="traveller"
          occupancies={occupancies}
          saleUnit={formattedSaleUnit}
        />
      </BodyText>
    </Group>
  )

  const occupancyPicker = (
    <OccupancyPicker
      occupancies={occupancies}
      onChange={onChange}
      disableSaleUnit={disableSaleUnit}
      saleUnit={saleUnit}
      {...occupancyPickerProps}
    />
  )

  return (
    <>
      {!useFullScreenMobileModal && (
        <DropdownSheet
          size={dropdownSize ?? 'L'}
          anchorRef={anchorRef}
          boundaryRef={boundaryRef}
          triggerRef={triggerRef}
          placement={placement ?? 'bottom-start'}
          portaled={portaled}
          open={isOpen}
          onClose={onClose}
          title={title}
          primaryActionProps={{
            type: 'button',
            children: applyLabel,
            onClick: handleApply,
          }}
          footerStart={occupancySummary}
        >
          {occupancyPicker}
        </DropdownSheet>
      )}
      {useFullScreenMobileModal && (
        <Modal
          isOpen={isOpen}
          size="L"
          height="full"
          flush
          flushVerticalTop
          title={title}
          primaryActionText={applyLabel}
          onPrimaryActionClick={handleApply}
          onClose={handleModalClose}
        >
          <Group direction="vertical" gap={24}>
            {occupancyPicker}
            <MobileModalSummaryInset>{occupancySummary}</MobileModalSummaryInset>
          </Group>
        </Modal>
      )}
      <Backdrop
        portalless
        visible={isOpen && !useFullScreenMobileModal}
        onClick={onClose}
      />
    </>
  )
}

export default TravellerSelectionModal
